import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import Dropdown from "../Drowpdown/dropdown";

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters: {
    status: string;
    createdBy: string;
    locationId: string;
    consignorId: string;
    packageId: string;
  };
  handleClearFilters: (filterForm: FilterDrawerProps["currentFilters"]) => void;
  handleApplyFilters: (filterForm: FilterDrawerProps["currentFilters"]) => void;
  packages: { packageId: number; packageName: string }[];
  consignors: { consignorId: number; consignorName: string }[];
  locations: { locationId: number; locationName: string }[];
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  currentFilters,
  handleClearFilters,
  handleApplyFilters,
  packages,
  consignors,
  locations,
}) => {
  const userId = useSelector((state: RootState) => state.auth.userId);

  // Initialize draft filters with current applied filters
  const [draftFilters, setDraftFilters] = useState(currentFilters);

  // Sync draft filters when drawer opens or current filters change
  useEffect(() => {
    if (isOpen) {
      setDraftFilters(currentFilters);
    }
  }, [isOpen, currentFilters]);

  const handleFilterChange = (key: string, value: string) => {
    setDraftFilters({
      ...draftFilters,
      [key]: value,
    });
  };

  const clearFilters = () => {
    const clearFilterForm = {
      status: "all",
      createdBy: userId || "",
      locationId: "",
      consignorId: "",
      packageId: "",
    };
    setDraftFilters(clearFilterForm);
    handleClearFilters(clearFilterForm);
  };

  const applyFilters = () => {
    handleApplyFilters(draftFilters);
  };
  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FaTimes size={20} />
            </button>
            <h2 className="text-xl font-bold text-gray-800 text-center w-full">
              Filters
            </h2>
            <button
              onClick={clearFilters}
              className="text-gray-500 hover:text-gray-700 transition-colors rounded-full px-4 py-2 text-sm font-bold"
            >
              Reset
            </button>
          </div>

          {/* Item Scope Filter - My Items vs All Items */}
          <div>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleFilterChange("createdBy", userId || "")}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${
                  draftFilters.createdBy === userId
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                My Items
              </button>
              <button
                onClick={() => handleFilterChange("createdBy", "allItems")}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${
                  draftFilters.createdBy === "allItems"
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                All Items
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6  max-h-[60dvh]  overflow-y-auto">
          {/* Status Filter - Toggle Style */}
          <div className="mb-6 border-b border-gray-200 pb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Item Status
            </label>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleFilterChange("status", "all")}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${
                  draftFilters.status === "all"
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange("status", "pending")}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${
                  draftFilters.status === "pending"
                    ? "bg-primary text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Pending
              </button>
            </div>
          </div>
          <div className="mb-6 border-b border-gray-200 pb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Location
            </label>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <Dropdown
                options={locations.map((location: { locationId: number; locationName: string }) => ({
                  value: location.locationId.toString(),
                  label: location.locationName,
                }))}
                onSelect={(option) =>
                  handleFilterChange("locationId", option.value)
                }
                value={draftFilters.locationId}
              />
            </div>
          </div>
          <div className="mb-6 border-b border-gray-200 pb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Consignor
            </label>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <Dropdown
                options={consignors.map((consignor: { consignorId: number; consignorName: string }) => ({
                  value: consignor.consignorId.toString(),
                  label: consignor.consignorName,
                }))}
                onSelect={(option) =>
                  handleFilterChange("consignorId", option.value)
                }
                value={draftFilters.consignorId}
              />
            </div>
          </div>
          <div className="mb-6 border-b border-gray-200 pb-6">
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Package
            </label>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <Dropdown
                options={packages.map((pck: { packageId: number; packageName: string }) => ({
                  value: pck.packageId.toString(),
                    label: pck.packageName,
                  }))}
                onSelect={(option) =>
                  handleFilterChange("packageId", option.value)
                }
                value={draftFilters.packageId}
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-3">
            <button
              onClick={applyFilters}
              className="flex-1 px-4 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterDrawer;
